import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { preReleaseService, PreReleaseProject } from '../../services/preReleaseService';
import { PricingService } from '../../services/pricingService';

import { InterestModal } from '../../components/prerelease/InterestModal';
import PreReleaseSetupStatus from '../../components/prerelease/PreReleaseSetupStatus';
import BasePageLayout from '../../components/BasePageLayout';
import { Avatar } from '../../components/common/Avatar';

import '../../styles/shared/dashboard.css';
import '../../styles/pages/TasksHubModern.css';
import '../../styles/pages/PreReleaseProjects.css';
import '../../styles/components/prerelease/PreReleaseModals.css';
import '../../styles/components/prerelease/PreReleaseSetupStatus.css';
import '../../styles/components/prerelease/PreReleaseProjectCardWrapper.css';

export const PreReleaseProjectsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<PreReleaseProject[]>([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState<'all' | 'apps' | 'websites' | 'products' | 'services'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pre-release' | 'concept' | 'development' | 'released'>('pre-release');
  const [sortBy, setSortBy] = useState<'newest' | 'popular' | 'price'>('newest');
  const [setupRequired, setSetupRequired] = useState(false);
  const [selectedProject, setSelectedProject] = useState<PreReleaseProject | null>(null);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(''); // Initialize with empty string to prevent uncontrolled to controlled warning

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setSetupRequired(false);

      // Set up warning detection BEFORE the API call
      const originalConsoleWarn = console.warn;
      let setupWarningDetected = false;
      console.warn = (...args) => {
        if (args[0]?.includes('Pre-release tables not found')) {
          setupWarningDetected = true;
        }
        originalConsoleWarn(...args);
      };

      // Single API call
      const data = await preReleaseService.getActiveProjects();

      // Restore original console.warn
      console.warn = originalConsoleWarn;

      if (setupWarningDetected) {
        setSetupRequired(true);
        return;
      }

      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
      // If we get a database error, it's likely setup is required
      setSetupRequired(true);
    } finally {
      setLoading(false);
    }
  };



  const filteredProjects = projects.filter(project => {
    // Apply type filter
    if (filter !== 'all' && project.project_type !== filter.slice(0, -1)) {
      return false;
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'pre-release') {
        // Show only concept and development projects (exclude released)
        if (project.status === 'released') {
          return false;
        }
      } else if (project.status !== statusFilter) {
        return false;
      }
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      return project.title.toLowerCase().includes(searchLower) ||
        project.description.toLowerCase().includes(searchLower) ||
        project.project_category?.toLowerCase().includes(searchLower);
    }

    return true;
  });

  // Reset function to clear all filters and search
  const resetFilters = () => {
    setFilter('all');
    setStatusFilter('pre-release');
    setSortBy('newest');
    setSearchTerm('');
  };

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'popular':
        // This would need interest count from the database
        return 0;
      case 'price':
        return (a.proposed_price || 0) - (b.proposed_price || 0);
      default:
        return 0;
    }
  });

  const handleProjectClick = (project: PreReleaseProject) => {
    setSelectedProject(project);
    setShowProjectDetail(true);
  };

  const handleCloseDetails = () => {
    console.log('close details handle is called')
    setSelectedProject(null);
    setShowProjectDetail(false);
  };

  if (loading) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="tasks-hub-container">
          {/* Modern Header with Search */}
          <div className="tasks-hub-header">
            <div className="tasks-hub-header-content">
              {/* Search Section */}
              <div className="tasks-hub-search-section">
                <div className="tasks-hub-search-bar">
                  <i className="bi bi-search tasks-hub-search-icon"></i>
                  <input
                    type="text"
                    placeholder={t('preRelease.searchPlaceholder')}
                    value=""
                    disabled
                    className="tasks-hub-search-input"
                    onChange={() => { }} // No-op to prevent React warning
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area - Centered Loading */}
          <div className="tasks-hub-main-two-column">
            <div className="tasks-hub-task-list-column">
              <div className="loading-container">
                <div className="loading-ring"></div>
                <p>{t('preRelease.loadingProjects')}</p>
              </div>
            </div>
            <div className="pre-release-projects-hub-project-details-column">
              <div className="project-details-placeholder">
                <div className="placeholder-icon">🚀</div>
                <h3>{t('preRelease.loading')}</h3>
                <p>{t('preRelease.pleaseWait')}</p>
              </div>
            </div>
          </div>
        </div>
      </BasePageLayout>
    );
  }

  // Show setup status if database setup is required
  if (setupRequired) {
    return <PreReleaseSetupStatus onRetry={loadProjects} />;
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className={`tasks-hub-container ${showProjectDetail ? 'detail-panel-open' : ''}`}>
        {/* Modern Header with Search */}
        <div className="tasks-hub-header">
          <div className="tasks-hub-header-content">
            {/* Search Section */}
            <div className="tasks-hub-search-section">
              <div className="tasks-hub-search-bar">
                <i className="bi bi-search tasks-hub-search-icon"></i>
                <input
                  type="text"
                  placeholder={t('preRelease.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="tasks-hub-search-input"
                />
              </div>

              {/* Action Buttons Row */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Quick Filter Buttons - Compact */}
                <button
                  className={`quick-filter-btn compact ${statusFilter === 'pre-release' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('pre-release')}
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  <i className="bi bi-lightbulb" style={{ fontSize: '14px' }}></i>
                  {t('preRelease.preReleaseFilter')}
                </button>
                <button
                  className={`quick-filter-btn available-now compact ${statusFilter === 'released' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('released')}
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  <i className="bi bi-cart-check" style={{ fontSize: '14px' }}></i>
                  {t('preRelease.availableNow')}
                </button>

                {user && (
                  <button
                    className="stat-item-small create-task-stat"
                    onClick={() => navigate('/create-pre-release-project')}
                    style={{ textDecoration: 'none', border: 'none', background: 'none', cursor: 'pointer' }}
                  >
                    <div className="stat-icon-small amber">
                      <i className="bi bi-plus-circle"></i>
                    </div>
                    <div className="stat-text-small">
                      <span className="stat-label-small">{t('preRelease.publish')}</span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="tasks-hub-filters">
              {/* Filter Dropdowns - No Labels */}
              <div className="filter-dropdowns">
                <div className="filter-group">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    className="filter-select"
                  >
                    <option value="all">{t('preRelease.allTypes')}</option>
                    <option value="apps">{t('preRelease.apps')}</option>
                    <option value="websites">{t('preRelease.websites')}</option>
                    <option value="products">{t('preRelease.products')}</option>
                    <option value="services">{t('preRelease.services')}</option>
                  </select>
                </div>

                <div className="filter-group">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="filter-select"
                  >
                    <option value="all">{t('preRelease.allStatus')}</option>
                    <option value="pre-release">{t('preRelease.preReleaseFilter')}</option>
                    <option value="concept">{t('preRelease.concept')}</option>
                    <option value="development">{t('preRelease.inDevelopment')}</option>
                    <option value="released">{t('preRelease.released')}</option>
                  </select>
                </div>

                <div className="filter-group">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="filter-select"
                  >
                    <option value="newest">{t('preRelease.newestFirst')}</option>
                    <option value="popular">{t('preRelease.mostPopular')}</option>
                    <option value="price">{t('preRelease.priceLowToHigh')}</option>
                  </select>
                </div>

                {/* Reset Button */}
                <button
                  className="reset-filters-btn"
                  onClick={resetFilters}
                  title={t('preRelease.clearAllFilters')}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  {t('preRelease.reset')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area - Two Column Layout */}
        <div
          className={`tasks-hub-main-two-column ${showProjectDetail ? 'detail-panel-open' : ''}`}
          onClick={showProjectDetail ? handleCloseDetails : undefined}
        >
          {/* Projects List Column */}
          <div className="tasks-hub-task-list-column">
            <div className="tasks-hub-task-list-header">
              <div className="tasks-hub-results-info">
                <span className="tasks-hub-results-count">
                  {sortedProjects.length} {sortedProjects.length === 1 ? t('preRelease.project') : t('preRelease.projects')}
                </span>
              </div>
            </div>

            <div className="pre-release-projects-hub-project-cards">
              {sortedProjects.length === 0 ? (
                <div className="pre-release-projects-hub-empty-state">
                  <div className="pre-release-projects-hub-empty-icon">🚀</div>
                  <h4>{t('preRelease.noProjectsFound')}</h4>
                  <p>{t('preRelease.tryAdjustingFilters')}</p>
                </div>
              ) : (
                sortedProjects.map(project => (
                  <div key={project.id} className="pre-release-project-card-wrapper">
                    <div
                      className={`pre-release-card-compact ${selectedProject?.id === project.id ? 'selected' : ''}`}
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className="project-card-header">
                        <div className="project-type">
                          <span className="type-icon">{getTypeIcon(project.project_type)}</span>
                          <span className="type-text">{project.project_type}</span>
                        </div>
                        <div className="project-card-status-area">
                          <div className="publisher-info">
                            <Avatar
                              src={project.publisher?.avatar_url}
                              username={project.publisher?.username}
                              className="publisher-avatar"
                              size={28}
                            />
                            <span className="publisher-name">{project.publisher?.username}</span>
                          </div>
                          {project.status === 'released' && (
                            <div className="project-status-indicator released">
                              <i className="bi bi-check-circle-fill"></i>
                              <span>{t('preRelease.availableNow')}</span>
                            </div>
                          )}
                          {project.status === 'development' && (
                            <div className="project-status-indicator development">
                              <i className="bi bi-gear-fill"></i>
                              <span>{t('preRelease.inDevelopment')}</span>
                            </div>
                          )}
                        </div>

                      </div>

                      <div className="project-card-content">
                        <h3 className="project-card-title">{project.title}</h3>
                        <p className="project-card-description">{project.description}</p>

                        <div className="project-card-meta">
                          <div className="project-card-price">
                            {PricingService.getCardPricingText(project)}
                          </div>
                          <div className="project-card-category">
                            {project.project_category || project.project_type}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Overlay for when detail panel is open */}
          {showProjectDetail && (
            <div className="project-detail-overlay" onClick={handleCloseDetails}></div>
          )}

          {/* Project Detail Panel - Slide out from right */}
          <div className={`project-detail-panel ${showProjectDetail ? 'panel-open' : ''}`}>
            {selectedProject && (
              <div className="project-detail-content">
                <div className="project-detail-header">
                  <div className="project-detail-header-main">
                    <div className="project-detail-title-area">
                      <h1 className="project-detail-title">{selectedProject.title}</h1>
                    </div>
                    <div className="project-detail-header-actions">
                      <button
                        className="project-detail-action-btn new-tab"
                        onClick={() => window.open(`/pre-release/${selectedProject.id}`, '_blank')}
                        title={t('preRelease.openInNewTab')}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                      <button
                        className="project-detail-action-btn close"
                        onClick={handleCloseDetails}
                        title={t('preRelease.close')}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="project-detail-body">
                  <ProjectDetailsView
                    project={selectedProject}
                    onInterestUpdate={loadProjects}
                  />
                </div>
              </div>
            )}
          </div>
        </div>


      </div>
    </BasePageLayout>
  );

  function getTypeIcon(type: string) {
    switch (type) {
      case 'app': return '📱';
      case 'website': return '🌐';
      case 'product': return '📦';
      case 'service': return '⚙️';
      default: return '💡';
    }
  }
};

// Project Details View Component
interface ProjectDetailsViewProps {
  project: PreReleaseProject;
  onInterestUpdate: () => void;
}

const ProjectDetailsView: React.FC<ProjectDetailsViewProps> = ({ project, onInterestUpdate }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [userInterest, setUserInterest] = useState<any>(null);
  const [interestSummary, setInterestSummary] = useState<any>(null);
  const [showInterestModal, setShowInterestModal] = useState(false);


  useEffect(() => {
    if (user) {
      loadUserInterest();
    }
    loadInterestSummary();
  }, [user, project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserInterest = async () => {
    try {
      const interest = await preReleaseService.getUserInterest(project.id);
      setUserInterest(interest);
    } catch (error) {
      console.error('Error loading user interest:', error);
    }
  };

  const loadInterestSummary = async () => {
    try {
      const summary = await preReleaseService.getProjectInterestSummary(project.id);
      setInterestSummary(summary);
    } catch (error) {
      console.error('Error loading interest summary:', error);
    }
  };

  const handleInterestSubmitted = async (interest: any) => {
    try {
      // Update the user interest state immediately for responsive UI
      setUserInterest(interest);
      setShowInterestModal(false);

      // Optionally reload interest summary and trigger update callback
      // These are non-blocking to maintain responsive UI
      loadInterestSummary();
      onInterestUpdate();
    } catch (error) {
      console.error('Error updating UI after interest submission:', error);
      // Even if there's an error, we still want to close the modal
      setShowInterestModal(false);
    }
  };

  // Use centralized pricing service
  const getPricingDisplay = () => {
    return PricingService.generatePricingDisplay(project);
  };

  const getDetailedPricingText = () => {
    return PricingService.getDetailedPricingText(project, t);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'app': return '📱';
      case 'website': return '🌐';
      case 'product': return '📦';
      case 'service': return '⚙️';
      default: return '💡';
    }
  };

  const handleImageClick = (src: string, alt: string) => {
    window.open(src, '_blank');
  };

  // Helper function to get feature description
  const getFeatureDescription = (feature: string | { text: string; screenshot_url?: string }, index: number): string => {
    const featureText = typeof feature === 'string' ? feature : feature.text;

    // You can customize these descriptions based on common feature patterns
    const descriptions = [
      t('preRelease.featureDescription1'),
      t('preRelease.featureDescription2'),
      t('preRelease.featureDescription3'),
      t('preRelease.featureDescription4'),
      t('preRelease.featureDescription5')
    ];

    // Try to generate contextual descriptions based on feature content
    if (featureText.toLowerCase().includes('user') || featureText.toLowerCase().includes('interface')) {
      return t('preRelease.featureDescriptionUser');
    } else if (featureText.toLowerCase().includes('security') || featureText.toLowerCase().includes('secure')) {
      return t('preRelease.featureDescriptionSecurity');
    } else if (featureText.toLowerCase().includes('performance') || featureText.toLowerCase().includes('fast')) {
      return t('preRelease.featureDescriptionPerformance');
    } else if (featureText.toLowerCase().includes('integration') || featureText.toLowerCase().includes('api')) {
      return t('preRelease.featureDescriptionIntegration');
    } else if (featureText.toLowerCase().includes('mobile') || featureText.toLowerCase().includes('responsive')) {
      return t('preRelease.featureDescriptionMobile');
    }

    return descriptions[index % descriptions.length];
  };

  // Helper function to get feature screenshot
  const getFeatureScreenshot = (feature: string | { text: string; screenshot_url?: string }, index: number): string | null => {
    // Handle new format (object with screenshot_url)
    if (typeof feature === 'object' && feature.screenshot_url) {
      return feature.screenshot_url;
    }

    // Handle JSON string format
    if (typeof feature === 'string') {
      try {
        const parsed = JSON.parse(feature);
        if (parsed && typeof parsed === 'object' && parsed.screenshot_url) {
          return parsed.screenshot_url;
        }
      } catch (e) {
        // Not a JSON string, continue with other checks
      }
    }

    // Handle old format - check if project has feature_screenshots array
    if (project.feature_screenshots && project.feature_screenshots[index]) {
      return project.feature_screenshots[index].url;
    }

    // Fallback to main project image or demo screenshots
    if (project.image_url && index === 0) {
      return project.image_url;
    }

    // Return null if no screenshot available
    return null;
  };

  return (
    <div className="project-details-view">
      {/* Project Header */}
      <div className="project-detail-header">
        <div className="project-meta-row-1">
          <div className="project-type">
            <span className="type-icon">{getTypeIcon(project.project_type)}</span>
            <span className="type-text">{project.project_type}</span>
          </div>
          <h1 className="project-title">{project.title}</h1>
          <div className="publisher-info">
            <Avatar
              src={project.publisher?.avatar_url}
              username={project.publisher?.username}
              className="publisher-avatar"
              size={28}
            />
            <span className="publisher-name">{t('preRelease.by')} {project.publisher?.username}</span>
          </div>
        </div>

        <div className="project-meta-row-2">
          <div className="project-price">
            <span className="price-label">{t('preRelease.price')}</span>
            <span className="price-value">{PricingService.getCardPricingText(project)}</span>
          </div>
          <div className="project-status-info">
            <span className="status-label">{t('preRelease.status')}:</span>
            <span className={`status-badge ${project.status}`}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>
          </div>
          {project.project_category && (
            <div className="project-category">{project.project_category}</div>
          )}
        </div>
      </div>

      {/* Project Image - Only show if image exists */}
      {project.image_url && (
        <div
          className="project-image clickable"
          onClick={() => handleImageClick(project.image_url!, project.title)}
        >
          <img
            src={project.image_url}
            alt={project.title}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const container = target.parentElement;
              if (container) {
                container.style.display = 'none';
              }
            }}
          />
        </div>
      )}

      {/* Project Description */}
      <div className="project-description">
        <h3>{t('preRelease.aboutThisProject')}</h3>
        <p>{project.description}</p>
      </div>

      {/* Concept Details */}
      {project.concept_details && (
        <div className="concept-details-enhanced">
          <h3>{t('preRelease.detailedConcept')}</h3>
          <div className="concept-text-enhanced">
            {project.concept_details.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      )}

      {/* Key Features with Screenshots */}
      {project.key_features && project.key_features.length > 0 && (
        <div className="key-features">
          <h3>{t('preRelease.keyFeaturesTitle')}</h3>
          <div className="key-features-enhanced">
            {project.key_features.map((feature, index) => {
              // Handle different data formats
              let featureText: string;

              if (typeof feature === 'string') {
                // Check if it's a JSON string that needs parsing
                try {
                  const parsed = JSON.parse(feature);
                  if (parsed && typeof parsed === 'object' && parsed.text) {
                    featureText = parsed.text;
                  } else {
                    featureText = feature;
                  }
                } catch (e) {
                  // Not a JSON string, treat as regular string
                  featureText = feature;
                }
              } else if (typeof feature === 'object' && feature !== null) {
                // Already an object
                featureText = feature.text || String(feature);
              } else {
                // Fallback
                featureText = String(feature);
              }

              // Clean up the feature text - remove any remaining JSON artifacts
              if (featureText.includes('{"text":') || featureText.includes('"screenshot_url":')) {
                // This looks like malformed JSON, try to extract just the text part
                const textMatch = featureText.match(/"text":"([^"]+)"/);
                if (textMatch) {
                  featureText = textMatch[1];
                } else {
                  // If we can't extract text, use a generic feature name
                  featureText = `Feature ${index + 1}`;
                }
              }

              return (
                <div key={index} className="feature-item">
                  <div className="feature-content">
                    <h4 className="feature-title">{featureText}</h4>
                    <p className="feature-description">
                      {getFeatureDescription(feature, index)}
                    </p>
                  </div>
                  <div className="feature-screenshot">
                    {(() => {
                      const screenshotUrl = getFeatureScreenshot(feature, index);
                      return screenshotUrl ? (
                        <div
                          className="feature-screenshot-image clickable"
                          onClick={() => handleImageClick(screenshotUrl, `${featureText} screenshot`)}
                        >
                          <img
                            src={screenshotUrl}
                            alt={`${featureText} screenshot`}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const placeholder = target.parentElement?.querySelector('.feature-screenshot-placeholder');
                              if (placeholder) {
                                (placeholder as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className="feature-screenshot-placeholder">
                          <i className="bi bi-image"></i>
                          <span>Screenshot</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Screenshots Gallery */}
          {project.feature_screenshots && project.feature_screenshots.length > 0 && (
            <div className="feature-screenshots-gallery">
              <h4>{t('preRelease.screenshots')}</h4>
              <div className="screenshots-grid">
                {project.feature_screenshots.map((screenshot, index) => (
                  <div
                    key={index}
                    className="screenshot-item clickable"
                    onClick={() => handleImageClick(screenshot.url, screenshot.caption || `${t('preRelease.featureScreenshot')} ${index + 1}`)}
                  >
                    <img
                      src={screenshot.url}
                      alt={screenshot.caption || `${t('preRelease.featureScreenshot')} ${index + 1}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const container = target.parentElement;
                        if (container) {
                          container.innerHTML = `
                            <div class="screenshot-placeholder">
                              <i class="bi bi-image"></i>
                              <span>Screenshot ${index + 1}</span>
                            </div>
                          `;
                        }
                      }}
                    />
                    {screenshot.caption && (
                      <div className="screenshot-caption">
                        {screenshot.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Demo Link */}
      {project.demo_url && (
        <div className="demo-section">
          <h3>{t('preRelease.demoPreview')}</h3>
          <a
            href={project.demo_url}
            target="_blank"
            rel="noopener noreferrer"
            className="demo-link"
          >
            {t('preRelease.viewDemo')}
          </a>
        </div>
      )}

      {/* Interest Stats */}
      {interestSummary && (
        <div className="interest-stats-section">
          <h3>{t('preRelease.communityInterest')}</h3>
          <div className="interest-stats">
            <div className="stat">
              <span className="stat-number">{interestSummary.total_interested || 0}</span>
              <span className="stat-label">{t('preRelease.peopleInterested')}</span>
            </div>
            <div className="stat">
              <span className="stat-number">{interestSummary.willing_to_pay || 0}</span>
              <span className="stat-label">{t('preRelease.willPurchase')}</span>
            </div>
            {interestSummary.average_interest_level && (
              <div className="stat">
                <span className="stat-number">{interestSummary.average_interest_level}/5</span>
                <span className="stat-label">{t('preRelease.avgInterest')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pricing */}
      <div className="pricing-section">
        <h3>{t('preRelease.pricingInformation')}</h3>
        <div className="price-details">
          <div className="price-row">
            <span className="label">{t('preRelease.expectedPrice')}</span>
            <span className="price">{PricingService.getDetailedPricingText(project, t).originalText.replace('Original: ', '').replace('预期价格：', '')}</span>
          </div>

          {project.early_bird_discount && (
            <div className="price-row discount">
              <span className="label">{t('preRelease.earlyBirdPrice')}</span>
              <span className="discounted-price">
                {(() => {
                  const pricingText = getDetailedPricingText();
                  return pricingText?.earlyBirdText?.replace('Early Bird: ', '').replace('早鸟价格：', '') || 'N/A';
                })()}
              </span>
            </div>
          )}

          {project.early_bird_discount && (
            <div className="savings-info">
              {getDetailedPricingText()?.savingsText || t('preRelease.earlyBirdPricingAvailable')}
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="timeline-section">
        <h3>{t('preRelease.developmentTimeline')}</h3>
        <div className="timeline-info">
          {project.estimated_timeline ? (
            <div className="timeline-estimate">
              <span className="timeline-text">{project.estimated_timeline}</span>
            </div>
          ) : (
            <div className="timeline-tbd">{t('preRelease.timelineTBD')}</div>
          )}

          <div className="project-status">
            <span className="status-label">{t('preRelease.status')}:</span>
            <span className={`status-badge ${project.status}`}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="project-actions">
        <a
          href={`/pre-release/${project.id}`}
          className="view-full-details-btn"
        >
          {t('preRelease.viewFullDetails')}
        </a>
      </div>

      {/* Interest Modal */}
      {showInterestModal && (
        <InterestModal
          project={project}
          existingInterest={userInterest}
          onClose={() => setShowInterestModal(false)}
          onInterestSubmitted={handleInterestSubmitted}
        />
      )}


    </div>
  );
};